import React, { useEffect, useState, useRef } from "react";
import './custom-scrollbar.css';
import { NavLink, useLocation } from "react-router-dom";
import {
  IoPersonOutline, IoReceiptOutline
} from "react-icons/io5";
import { SlCalender } from "react-icons/sl";
import { MdOutlineDashboard } from "react-icons/md";
import { LuClipboardPlus, LuCalendarDays, LuCalendarMinus } from "react-icons/lu";
import { MdAttachMoney } from "react-icons/md";
import { motion, AnimatePresence } from "framer-motion";
import { CgNotes } from "react-icons/cg";
import {
  TbCalendarTime, TbClockPlus, TbChevronDown, TbChevronRight, TbUsersGroup,
  TbReportAnalytics
} from "react-icons/tb";
import { HiOutlineClipboardList, HiMenuAlt2 } from "react-icons/hi";
import { GrUserWorker } from "react-icons/gr";
import { Megaphone, TentTree } from "lucide-react";
import { useUser } from "../context/UserContext";
import { usePendingRequests } from '../context/PendingRequestContext';
import { usePendingCompOffRequests } from "../context/PendingCompOffRequestContext";
import { usePendingOnDutyRequests } from "../context/PendingOnDutyRequestContext";
import { usePendingCompPayRequests } from "../context/PendingCompPayRequestContext";
import { usePendingPayslipRequests } from "../context/PendingPaySlipRequestContext";
import api from "../utils/api";
import { FaIndianRupeeSign } from "react-icons/fa6";

function Employee() {
  const { currentUser, loading } = useUser();
  const { pendingCount, updatePendingCount } = usePendingRequests();
  const { pendingCompOffCount, updatePendingCompOffCount } = usePendingCompOffRequests();
  const { pendingOndutyCount, updatePendingOndutyCount } = usePendingOnDutyRequests();
  const { pendingCompPayCount, updatePendingCompPayCount } = usePendingCompPayRequests();
  const { pendingPayslipCount, updatePendingPayslipCount } = usePendingPayslipRequests();
  const location = useLocation();

  const [userRole, setUserRole] = useState(null);
  const [userOffice, setUserOffice] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [manualCollapse, setManualCollapse] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const [collapseState, setCollapseState] = useState({ apply: false, teamrequests: false, events: false });

  const navAreaRef = useRef(null);

  useEffect(() => {
    if (currentUser) {
      setUserRole(currentUser?.role);
      setUserOffice(currentUser?.office);
    }
  }, [currentUser]);

  useEffect(() => {
    const fetchPendingCount = async () => {
      if ((userRole === "MANAGER" || userRole === "CEO") && currentUser?.id) {
        const paramKey = userRole === "CEO" ? "ceoId" : "managerId";
        try {
          const [leaveRes, compOffRes, onDutyRes, compPayRes, payslipRes] = await Promise.all([
            api.get(`/api/leaves/pending-count?${paramKey}=${currentUser?.id}`),
            api.get(`/api/compoffs/pending-count?${paramKey}=${currentUser?.id}`),
            api.get(`/api/compoffs/pending-count?${paramKey}=${currentUser?.id}`),
            api.get(`/api/comppay/pending-count?${paramKey}=${currentUser?.id}`),
            api.get(`/payslip/approval/pending-count?${paramKey}=${currentUser?.id}`)
          ]);

          updatePendingCount(leaveRes.data);
          updatePendingCompOffCount(compOffRes.data);
          updatePendingOndutyCount(onDutyRes.data);
          updatePendingCompPayCount(compPayRes.data);
          updatePendingPayslipCount(payslipRes.data);
        } catch (err) {
          console.error("Error fetching pending counts:", err);
        }
      }
    };

    if (!loading && currentUser) {
      fetchPendingCount();
      const interval = setInterval(fetchPendingCount, 300000);
      return () => clearInterval(interval);
    }
  }, [userRole, currentUser, updatePendingCount, updatePendingCompOffCount, updatePendingOndutyCount, updatePendingCompPayCount, updatePendingPayslipCount, loading]);

  useEffect(() => {
    setIsExpanded(false);
  }, [location.pathname]);

  const applyLinks = [
    { name: "Leaves", link: "/employee/leaves", icon: LuCalendarMinus },
    // { name: "Comps", link: "/employee/comps", icon: FaIndianRupeeSign  },
    // { name: "On Duty", link: "/employee/on-duty", icon: GrUserWorker },  // Commented out for now
    { name: "Comp Off", link: "/employee/comp-off", icon: TbClockPlus },
    { name: "Comp Pay", link: "/employee/comp-pay", icon: FaIndianRupeeSign  }
  ];

  const eventsLinks = [
    { name: "Annoucements", link: "/employee/events/annoucements", icon: Megaphone },
    { name: "Holidays", link: "/employee/events/holidays", icon: TentTree }
  ];

  const teamRequestLinks = ((userRole === "MANAGER" || userRole === "CEO") && userOffice === "SPC") ? [
    { name: "Leave Requests", link: userRole === "CEO" ? "/ceo/leave-requests" : "/employee/leave-requests", icon: HiOutlineClipboardList, badge: pendingCount },
    { name: "Comp Requests", link: userRole === "CEO" ? "/ceo/comp-off-requests" : "/employee/comp-off-requests", icon: LuClipboardPlus, badge: pendingCompOffCount },
    { name: "Comp Pay Requests", link: userRole === "CEO" ? "/ceo/comp-pay-requests" : "/employee/comp-pay-requests", icon: FaIndianRupeeSign , badge: pendingCompPayCount },
    { name: "Payslip Requests", link: userRole === "CEO" ? "/ceo/payslip-requests" : "/employee/payslip-requests", icon: IoReceiptOutline, badge: pendingPayslipCount }
  ] : [];

  const totalTeamRequests = pendingCount + pendingCompOffCount + 0 + pendingCompPayCount + pendingPayslipCount;

  // For CEO, show only Team Requests section; for Manager/Employee show full navigation
  const navLinks = userRole === "CEO" ? (
    teamRequestLinks.length ? [{
      section: "Team Requests",
      links: teamRequestLinks,
      icon: TbUsersGroup,
      badge: totalTeamRequests
    }] : []
  ) : [
    { name: "Dashboard", link: "/employee/dashboard", icon: MdOutlineDashboard },
    { name: "Pay Slips", link: "/employee/payslips", icon: IoReceiptOutline },
    { name: "Attendance", link: "/employee/attendance", icon: LuCalendarDays },
    { section: "Events", links: eventsLinks, icon: TbCalendarTime },
    { section: "Apply", links: applyLinks, icon: CgNotes },
    ...(teamRequestLinks.length
      ? [{
        section: "Team Requests",
        links: teamRequestLinks,
        icon: TbUsersGroup,
        badge: totalTeamRequests
      }]
      : [])
  ];

  // Helper function to check if any child link is active
  const isParentActive = (links) => {
    return links.some(item => location.pathname === item.link);
  };

  const sidebarVariants = {
    open: { 
      width: "288px", 
      transition: { 
        type: "spring", 
        stiffness: 120, 
        damping: 20,
        mass: 0.8
      } 
    },
    closed: { 
      width: "80px", 
      transition: { 
        type: "spring", 
        stiffness: 120, 
        damping: 20,
        mass: 0.8
      } 
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.03,
        type: "spring",
        stiffness: 300,
        damping: 24
      }
    })
  };

  const submenuVariants = {
    hidden: { 
      opacity: 0, 
      height: 0,
      transition: {
        duration: 0.2     
      }
    },
    visible: { 
      opacity: 1, 
      height: "auto",
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  return (
    <div
      className="sidebar-hover"
      onMouseEnter={() => { if (!manualCollapse) setIsExpanded(true); }}
      onMouseLeave={() => { if (!manualCollapse) setIsExpanded(false); }}
    >
      <AnimatePresence>
        {showSidebar && (
          <motion.div
            initial={false}
            animate={isExpanded ? "open" : "closed"}
            variants={sidebarVariants}
            className="h-full bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 shadow-2xl flex flex-col items-center fixed top-0 left-0 z-40 border-r border-slate-700/50"
          >
            {/* Header with gradient overlay */}
            <div className="w-full px-4 py-5 bg-gradient-to-r from-slate-800/50 to-slate-700/30 backdrop-blur-sm border-b border-slate-700/50">
              <div className="flex items-center justify-between">
                <NavLink to="/" className="flex items-center overflow-hidden">
                  <motion.span 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: isExpanded ? 1 : 0 }}
                    transition={{ duration: 0.2 }}
                    className="text-xl font-semibold bg-gradient-to-r from-lime-400 to-lime-300 bg-clip-text text-transparent whitespace-nowrap"
                  >
{userRole === "CEO" ? "CEO Panel" : "Employee Panel"}          
        </motion.span>
                </NavLink>
                <motion.button 
                  onClick={() => {
                    setManualCollapse(!isExpanded);
                    setIsExpanded(prev => !prev);
                  }}
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.95 }}
                  className="text-slate-300 hover:text-white hover:bg-slate-700/50 p-2 rounded-lg transition-all duration-200"
                >
                  <HiMenuAlt2 size={24} />
                </motion.button>
              </div>
            </div>

            {/* Navigation */}
            <div
              ref={navAreaRef}
              className={`gap-2 flex flex-col items-start w-full mt-6 px-3 custom-scrollbar ${isExpanded ? '' : 'overflow-hidden'}`}
              style={{ maxHeight: 'calc(100vh - 120px)', overflowY: isExpanded ? 'auto' : 'hidden' }}
            >
              {navLinks.map((entry, index) => {
                const parentIsActive = entry.section && isParentActive(entry.links);
                
                return entry.section ? (
                  <motion.div 
                    key={index} 
                    className="w-full"
                    custom={index}
                    variants={itemVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    <motion.button
                      whileHover={{ scale: isExpanded ? 1.02 : 1.1 }}
                      whileTap={{ scale: 0.98 }}
                      className={`text-slate-200 flex items-center justify-between w-full px-3 py-3 text-left rounded-xl transition-all duration-200 relative group
                        ${parentIsActive 
                          ? "bg-gradient-to-r from-lime-600 to-lime-600 text-white shadow-lg shadow-lime-500/20" 
                          : "hover:bg-slate-700/50"}`}
                      onClick={() => {
                        const key = entry.section.toLowerCase().replace(/\s+/g, '');
                        setCollapseState(prev => ({ ...prev, [key]: !prev[key] }));
                      }}
                    >
                      <div className="flex items-center relative">
                        <div className={`p-2 rounded-lg transition-colors ${parentIsActive ? 'bg-white/20' : 'bg-slate-700/30 group-hover:bg-slate-600/50'}`}>
                          <entry.icon size={20} className={`transition-colors ${parentIsActive ? 'text-white' : 'text-slate-300 group-hover:text-white'}`} />
                        </div>

                        {/* Badge in collapsed mode */}
                        {!isExpanded && entry.section === "Team Requests" && entry.badge > 0 && (
                          <motion.span 
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="absolute -top-1 -right-1 bg-gradient-to-br from-red-500 to-red-600 text-white text-[10px] font-semibold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center shadow-lg"
                          >
                            {entry.badge}
                          </motion.span>
                        )}

                        {isExpanded && (
                          <motion.span 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="ml-3 text-base font-medium whitespace-nowrap"
                          >
                            {entry.section}
                          </motion.span>
                        )}
                      </div>

                      {/* Expanded badge */}
                      {isExpanded && entry.badge > 0 && (
                        <motion.span 
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="ml-2 bg-gradient-to-br from-red-500 to-red-600 text-white text-xs font-semibold rounded-full min-w-[22px] h-[22px] px-2 flex items-center justify-center shadow-lg"
                        >
                          {entry.badge}
                        </motion.span>
                      )}

                      {isExpanded && (
                        <motion.div
                          animate={{ rotate: collapseState[entry.section.toLowerCase().replace(/\s+/g, '')] ? 0 : -90 }}
                          transition={{ duration: 0.2 }}
                        >
                          <TbChevronDown size={18} className={parentIsActive ? "text-white" : "text-slate-400"} />
                        </motion.div>
                      )}
                    </motion.button>

                    {/* Submenu with animation */}
                    <AnimatePresence>
                      {isExpanded && collapseState[entry.section.toLowerCase().replace(/\s+/g, '')] && (
                        <motion.div 
                          variants={submenuVariants}
                          initial="hidden"
                          animate="visible"
                          exit="hidden"
                          className="pl-4 mt-1 space-y-1 overflow-hidden"
                        >
                          {entry.links.map((item, subIndex) => (
                            <motion.div 
                              key={subIndex} 
                              className="w-full"
                              custom={subIndex}
                              variants={itemVariants}
                              initial="hidden"
                              animate="visible"
                            >
                              <NavLink
                                to={item.link}
                                onClick={() => {
                                  setManualCollapse(true);
                                  setIsExpanded(false);
                                  setTimeout(() => setManualCollapse(false), 400);
                                }}
                                className={({ isActive }) =>
                                  `text-slate-300 w-full py-2.5 px-3 flex items-center rounded-lg relative transition-all duration-200 group
                                  ${isActive 
                                    ? "bg-gradient-to-r from-lime-600 to-lime-600 text-white shadow-lg shadow-lime-500/20" 
                                    : "hover:bg-slate-700/40 hover:translate-x-1"}`
                                }
                              >
                                {({ isActive }) => (
                                  <>
                                    <div className={`p-1.5 rounded-md transition-colors ${isActive ? 'bg-white/20' : 'bg-slate-700/30 group-hover:bg-slate-600/50'}`}>
                                      <item.icon size={16} className={isActive ? "text-white" : "text-slate-400 group-hover:text-slate-200"} />
                                    </div>
                                    <span className="ml-2.5 text-sm font-medium whitespace-nowrap">{item.name}</span>
                                    {item.badge > 0 && (
                                      <motion.span 
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        className="ml-auto bg-gradient-to-br from-red-500 to-red-600 text-white text-[10px] font-semibold rounded-full min-w-[18px] h-[18px] px-1.5 flex items-center justify-center shadow-lg"
                                      >
                                        {item.badge}
                                      </motion.span>
                                    )}
                                  </>
                                )}
                              </NavLink>
                            </motion.div>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ) : (
                  <motion.div 
                    key={index} 
                    className="w-full"
                    custom={index}
                    variants={itemVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    <NavLink
                      to={entry.link}
                      onClick={() => {
                        setManualCollapse(true);
                        setIsExpanded(false);
                        setTimeout(() => setManualCollapse(false), 400);
                      }}
                      className={({ isActive }) =>
                        `text-slate-200 w-full py-3 px-3 flex items-center rounded-xl relative transition-all duration-200 group
                        ${isActive 
                          ? "bg-gradient-to-r from-lime-600 to-lime-600 text-white shadow-lg shadow-lime-500/20" 
                          : "hover:bg-slate-700/50 hover:translate-x-1"}`
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <motion.div 
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                            className={`p-2 rounded-lg transition-colors ${isActive ? 'bg-white/20' : 'bg-slate-700/30 group-hover:bg-slate-600/50'}`}
                          >
                            <entry.icon size={20} className={isActive ? "text-white" : "text-slate-300 group-hover:text-white"} />
                          </motion.div>
                          {isExpanded && (
                            <motion.span 
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              className="ml-3 text-base font-medium whitespace-nowrap"
                            >
                              {entry.name}
                            </motion.span>
                          )}
                        </>
                      )}
                    </NavLink>
                  </motion.div>
                );
              })}
            </div>

            {/* Footer gradient effect */}
           
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default Employee;